import { ProductListComponentProps, ProductListEditorProps } from './ProductList.types';

/**
 * 문자열이나 숫자를 배열로 변환
 * @param value 배열, 문자열 또는 숫자
 * @returns 항상 배열 형태로 반환
 */
export function convertToArray(value: any): (string | number)[] {
  if (!value) return [];
  
  // 이미 배열인 경우
  if (Array.isArray(value)) return value;
  
  // 문자열이며 콤마가 있는 경우 (콤마로 구분된 값 목록)
  if (typeof value === 'string' && value.includes(',')) {
    return value.split(',').map(item => item.trim());
  }
  
  // 그 외 단일 값
  return [value];
}

/**
 * 기존 데이터 형식을 새로운 형식으로 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 새로운 컴포넌트용 props
 */
export function adaptProductListProps(originalProps: any): ProductListComponentProps {
  // 기본값 설정
  const componentProps = originalProps?.componentProps || {};
  const style = originalProps?.style || {};
  
  // 문자열이나 단일 값을 배열로 변환
  const include_product_ids = convertToArray(componentProps?.include_product_ids || []);
  const exclude_product_ids = convertToArray(componentProps?.exclude_product_ids || []);
  const include_category_ids = convertToArray(componentProps?.include_category_ids || []);
  const exclude_category_ids = convertToArray(componentProps?.exclude_category_ids || []);
  const excludeIndexes = convertToArray(componentProps?.excludeIndexes || []);
  
  // 변환된 componentProps 생성
  const adaptedComponentProps = {
    order: componentProps?.order || '',
    lg: componentProps?.lg !== undefined ? componentProps.lg : 12,
    className: componentProps?.className || 'padding-tb-40', 
    itemsPerRow: componentProps?.itemsPerRow || 4,
    categoryId: componentProps?.categoryId || '',
    excludeIndexes,
    showStock: componentProps?.showStock === true,
    include_product_ids,
    exclude_product_ids,
    include_category_ids,
    exclude_category_ids,
    itemsPerPage: componentProps?.itemsPerPage || 20,
    // 다른 componentProps 속성들 복사
    ...componentProps
  };
  
  // 최종 props 생성
  const adaptedProps: ProductListComponentProps = {
    id: originalProps?.id || '',
    style, 
    className: originalProps?.className || '',
    componentProps: adaptedComponentProps,
    isEditor: originalProps?.isEditor === true,
    viewMode: originalProps?.viewMode || 'pc'
  };
  
  // 원본 props의 다른 프로퍼티들 복사 (위에서 이미 처리한 것들 제외)
  const excludeKeys = ['id', 'style', 'className', 'componentProps', 'isEditor', 'viewMode'];
  
  Object.keys(originalProps || {}).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });
  
  return adaptedProps;
}

/**
 * 에디터용 데이터 형식 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 에디터 컴포넌트용 props
 */
export function adaptProductListEditorProps(originalProps: any): ProductListEditorProps {
  // 기본 props 변환 사용
  const adaptedProps = adaptProductListProps(originalProps);
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps?.isSelected || false,
    isPreview: originalProps?.isPreview || false,
    updateComponent: originalProps?.updateComponent || (() => {})
  };
} 